<template>
  <b-card body-bg-variant="light"
          header="Experimental infrastructure"
          header-bg-variant="dark"
          header-text-variant="light">

    <label for="beamline">Beamline</label>
    <b-form-select id="beamline"
                   v-model="data.general.beamlines"
                   :disabled="!editable"
                   @input.native="inputBeamlines">
      <option v-for="option in Object.keys(beamlines)"
              :key="option"
              :value="option">{{ option }}
      </option>
    </b-form-select>

    <label for="endStation">End station</label>
    <b-form-select id="endStation"
                   v-model="data.general.end_station"
                   :disabled="!editable"
                   @input.native="inputEndStation">
      <option v-for="option in beamlines[data.general.beamlines]"
              :key="option"
              :value="option">{{ option }}
      </option>
    </b-form-select>

    <template v-if="isProposalRapidAccess">
      <template v-if="isSubmissionCurrentlyBlocked">
        <b-alert
          :show="true"
          class="alert"
          variant="danger">
          <h5 class="font-weight-bold mb-0">
            You cannot submit RA Scientific merit proposal on SMAUG beamline.
            Please change the RA subtype in the 'General information' part above.
          </h5>
        </b-alert>
        <hr>
      </template>
      <label for="shiftsMinimal"
             style="width: 100%">
        Minimal shifts requested
        <span class="grey--text">
          (1 to {{ maxShiftsAllowed }})
        </span>
        <b-form-input id="shiftsMinimal"
                      v-model="data.general.shifts_minimal"
                      :class="{ 'custom-invalid-input-border custom-input-border-radius': $data.$v.data.general.shifts_minimal.$error }"
                      :max="maxShiftsAllowed"
                      :readonly="!editable"
                      min="1"
                      placeholder="Enter missing value"
                      type="number"
                      @input="inputRapidAccessShifts"
                      @paste.prevent/>
      </label>

      <label for="shiftsOptimal"
             style="width: 100%">
        Optimal shifts requested
        <span class="grey--text">
          (1 to {{ maxShiftsAllowed }})
        </span>
        <b-form-input id="shiftsOptimal"
                      v-model="data.general.shifts_optimal"
                      :class="{ 'custom-invalid-input-border custom-input-border-radius': $data.$v.data.general.shifts_optimal.$error }"
                      :max="maxShiftsAllowed"
                      :readonly="!editable"
                      min="1"
                      placeholder="Enter missing value"
                      type="number"
                      @input="inputRapidAccessShifts"
                      @paste.prevent/>
      </label>
    </template>

    <template v-else>
      <label for="shiftsRequested"
             style="width: 100%">
        Shifts requested
        <span class="grey--text">
          (1 to {{ maxShiftsAllowed }})
        </span>
        <b-form-input id="shiftsRequested"
                      v-model="data.general.shifts"
                      :class="{ 'custom-invalid-input-border custom-input-border-radius': $data.$v.data.general.shifts.$error }"
                      :max="maxShiftsAllowed"
                      :readonly="!editable"
                      min="1"
                      placeholder="Enter missing value"
                      type="number"
                      @input="inputShifts"
                      @paste.prevent/>
      </label>

      <shifts-schedule v-if="isProposalLongTerm"
                       v-model="data.general.shifts_schedule"
                       :editable="editable"
                       :shiftsScheduleValidation="$data.$v.data.general.shifts_schedule"
                       infrastructure="synchrotron"/>
    </template>

  </b-card>
</template>

<script>
  import ShiftsSchedule from '@/components/proposal/ShiftsSchedule';
  import { isProposalOfType } from '@/helpers/proposal-form';
  import beamlines from '@/json/beamlines';
  import { longTermSpecification, proposalTypesWithMaxShifts } from '@/json/beamlineSpecification';

  export default {
    name: 'ExperimentalInfrastructureForm',
    components: { ShiftsSchedule },
    props: {
      value: undefined,
      validation: undefined,
      editable: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        beamlines,
        data: this.value,
        $v: this.validation,
        proposalTypesWithMaxShifts,
        longTermSpecification,
      };
    },
    computed: {
      maxShiftsAllowed() {
        const currentProposalTypeMaxShifts = this.proposalTypesWithMaxShifts[this.data.general.proposal_type];
        const maxShiftsDefault = this.proposalTypesWithMaxShifts.Standard;
        return currentProposalTypeMaxShifts || maxShiftsDefault;
      },
      isProposalLongTerm() {
        return isProposalOfType(this.data.general, 'Long term');
      },
      isProposalRapidAccess() {
        return isProposalOfType(this.data.general, 'Rapid access');
      },
      isSubmissionCurrentlyBlocked() {
        if (this.isProposalRapidAccess) {
          return this.data.general.rapid_access_subtype === 'Scientific merit'
            && this.data.general.end_station === 'SMAUG - SAXS';
        }
        return false;
      },
    },
    methods: {
      inputBeamlines() {
        this.resetEndStation();
        this.$data.$v.data.general.beamlines.$touch();
      },
      inputEndStation() {
        this.resetBeamlineTab();
        this.$data.$v.data.general.end_station.$touch();
      },
      resetEndStation() {
        // eslint-disable-next-line camelcase
        if (this.data.general.end_station !== undefined) this.data.general.end_station = '';
        this.data.environment = {};
      },
      resetBeamlineTab() {
        if (this.data.general.end_station !== undefined) this.data.environment = {};
      },
      inputShifts() {
        this.$data.$v.data.general.shifts.$touch();
        this.$data.$v.data.general.shifts_schedule.$touch();
      },
      inputRapidAccessShifts() {
        this.$data.$v.data.general.shifts_minimal.$touch();
        this.$data.$v.data.general.shifts_optimal.$touch();
      },
    },
    watch: {
      value: {
        handler() {
          this.data = this.value;
          if (this.data.general === undefined) this.data.general = {};
        },
        deep: true,
      },
      'data.general.beamlines': {
        handler() {
          const chosenBeamlineEndStations = this.beamlines[this.data.general.beamlines];
          if (chosenBeamlineEndStations.length === 1) {
            this.$set(this.data.general, 'end_station', ...chosenBeamlineEndStations);
          }
        },
      },
      isProposalRapidAccess: {
        handler(newValue) {
          if (newValue) {
            delete this.data.general.shifts;
          } else {
            delete this.data.general.shifts_minimal;
            delete this.data.general.shifts_optimal;
          }
        },
      },
      isProposalLongTerm: {
        handler(newValue) {
          if (!newValue) {
            delete this.data.general.shifts_schedule;
          } else if (!this.data.general.shifts_schedule) {
            const emptyShiftsSchedule = Array(this.longTermSpecification.numberOfCallsToPlan);
            this.$set(this.data.general, 'shifts_schedule', emptyShiftsSchedule);
          }
        },
      },
    },
  };
</script>
